import { useState } from 'react';

type VideoMode = 'text-to-video' | 'image-to-video' | 'animate-still' | 'lip-sync' | 'add-sound' | 'upscale';

interface VideoJob {
  id: string;
  mode: VideoMode;
  prompt: string;
  status: 'queued' | 'processing' | 'done';
  progress: number;
  thumbnail: string;
}

const VIDEO_MODES: { id: VideoMode; label: string; icon: string; desc: string; color: string }[] = [
  { id: 'text-to-video', label: 'Text to Video', icon: 'ri-film-line', desc: 'Generate architectural walkthroughs from text prompts', color: 'from-pink-500/20 to-rose-500/20' },
  { id: 'image-to-video', label: 'Image to Video', icon: 'ri-image-2-line', desc: 'Animate your rendered images into cinematic clips', color: 'from-orange-500/20 to-amber-500/20' },
  { id: 'animate-still', label: 'Animate Still', icon: 'ri-magic-line', desc: 'Bring static renders to life with subtle motion', color: 'from-teal-500/20 to-cyan-500/20' },
  { id: 'lip-sync', label: 'Lip Sync', icon: 'ri-mic-line', desc: 'Add narration sync to presentation avatars', color: 'from-violet-500/20 to-purple-500/20' },
  { id: 'add-sound', label: 'Add Sound', icon: 'ri-volume-up-line', desc: 'Layer ambient audio onto your design videos', color: 'from-emerald-500/20 to-green-500/20' },
  { id: 'upscale', label: 'Upscale Video', icon: 'ri-hd-line', desc: 'Enhance resolution and quality of existing clips', color: 'from-sky-500/20 to-blue-400/20' },
];

const SAMPLE_THUMBNAILS = [
  'https://public.readdy.ai/ai/img_res/2340005f37573f9f27371b38791b59e8.jpg',
  'https://readdy.ai/api/search-image?query=smooth%20camera%20walkthrough%20modern%20minimalist%20interior%20living%20room%20natural%20light%20flowing%20curtains%20architectural%20animation%20still%20frame%20cinematic&width=400&height=225&seq=vid-thumb2&orientation=landscape',
  'https://readdy.ai/api/search-image?query=architectural%20visualization%20video%20still%20frame%20contemporary%20house%20exterior%20twilight%20lighting%20dramatic%20sky%20reflections%20pool%20water&width=400&height=225&seq=vid-thumb3&orientation=landscape',
];

const SOUND_PRESETS = [
  { id: 'nature', label: 'Nature Ambience', icon: 'ri-leaf-line' },
  { id: 'urban', label: 'Urban Atmosphere', icon: 'ri-building-line' },
  { id: 'cinematic', label: 'Cinematic Score', icon: 'ri-music-2-line' },
  { id: 'calm', label: 'Calm & Minimal', icon: 'ri-wind-line' },
];

interface AIVideoStudioProps {
  darkMode: boolean;
}

export default function AIVideoStudio({ darkMode }: AIVideoStudioProps) {
  const [activeMode, setActiveMode] = useState<VideoMode>('text-to-video');
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobs, setJobs] = useState<VideoJob[]>([]);
  const [selectedSound, setSelectedSound] = useState('cinematic');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [duration, setDuration] = useState(5);
  const [cameraMotion, setCameraMotion] = useState('orbit');
  const [videoToast, setVideoToast] = useState('');

  const showToast = (msg: string) => {
    setVideoToast(msg);
    setTimeout(() => setVideoToast(''), 3000);
  };

  const handleDownloadVideo = (thumbnail: string) => {
    const a = document.createElement('a');
    a.href = thumbnail;
    a.download = 'video-result.mp4';
    a.target = '_blank';
    a.click();
    showToast('Download started!');
  };

  const handleShareVideo = async (thumbnail: string) => {
    try {
      await navigator.clipboard.writeText(thumbnail);
      showToast('Video link copied to clipboard!');
    } catch {
      showToast('Link copied!');
    }
  };

  const handleGenerate = () => {
    if (!prompt && activeMode === 'text-to-video') return;
    const newJob: VideoJob = {
      id: Date.now().toString(),
      mode: activeMode,
      prompt: prompt || 'Architectural visualization',
      status: 'processing',
      progress: 0,
      thumbnail: SAMPLE_THUMBNAILS[jobs.length % SAMPLE_THUMBNAILS.length],
    };
    setJobs(prev => [newJob, ...prev]);
    setIsGenerating(true);

    let prog = 0;
    const interval = setInterval(() => {
      prog += Math.random() * 18 + 8;
      if (prog >= 100) {
        prog = 100;
        clearInterval(interval);
        setIsGenerating(false);
        setJobs(prev => prev.map(j => j.id === newJob.id ? { ...j, status: 'done', progress: 100 } : j));
      } else {
        setJobs(prev => prev.map(j => j.id === newJob.id ? { ...j, progress: Math.round(prog) } : j));
      }
    }, 400);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setUploadedImage(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-5">
      <div className="mb-2">
        <h3 className="text-base font-bold text-gray-900 mb-1">AI Video Studio</h3>
        <p className="text-xs text-gray-400">Powered by Nim Video — text-to-video, animate stills, lip sync &amp; more</p>
      </div>

      {/* Mode Selector */}
      <div className="grid grid-cols-2 gap-2">
        {VIDEO_MODES.map(mode => (
          <button
            key={mode.id}
            onClick={() => setActiveMode(mode.id)}
            className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left ${
              activeMode === mode.id
                ? 'border-pink-300 bg-pink-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${activeMode === mode.id ? 'bg-pink-100' : 'bg-gray-100'}`}>
              <i className={`${mode.icon} text-sm ${activeMode === mode.id ? 'text-pink-500' : 'text-gray-400'}`}></i>
            </div>
            <div className="min-w-0">
              <p className={`text-xs font-semibold whitespace-nowrap ${activeMode === mode.id ? 'text-pink-600' : 'text-gray-800'}`}>{mode.label}</p>
              <p className="text-xs text-gray-400 leading-tight mt-0.5 line-clamp-2">{mode.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Input Area */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
        {(activeMode === 'image-to-video' || activeMode === 'animate-still') && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">Source Image</p>
            {uploadedImage ? (
              <div className="relative rounded-lg overflow-hidden" style={{ height: '120px' }}>
                <img src={uploadedImage} alt="Source" className="w-full h-full object-cover" />
                <button
                  onClick={() => setUploadedImage(null)}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-pink-400 hover:bg-pink-50 transition-colors">
                <i className="ri-image-add-line text-2xl text-gray-400 mb-1"></i>
                <span className="text-xs text-gray-500">Upload render or image</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            )}
          </div>
        )}

        {activeMode !== 'add-sound' && activeMode !== 'upscale' && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">
              {activeMode === 'text-to-video' ? 'Video Prompt' : activeMode === 'lip-sync' ? 'Narration Script' : 'Motion Description'}
            </p>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder={
                activeMode === 'text-to-video'
                  ? 'e.g. Cinematic drone flyover of a modern farmhouse at golden hour...'
                  : activeMode === 'lip-sync'
                  ? 'e.g. Welcome to this stunning 4-bedroom contemporary home...'
                  : 'e.g. Gentle camera pan revealing the living room interior...'
              }
              className="w-full h-20 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-pink-400 resize-none"
              maxLength={300}
            />
            <p className="text-xs text-gray-400 mt-1">{prompt.length}/300</p>
          </div>
        )}

        {activeMode === 'add-sound' && (
          <div>
            <p className="text-xs text-gray-500 mb-3 font-medium">Sound Preset</p>
            <div className="grid grid-cols-2 gap-2">
              {SOUND_PRESETS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSound(s.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    selectedSound === s.id ? 'bg-pink-50 border border-pink-300 text-pink-600' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <i className={`${s.icon} text-sm`}></i>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Camera Motion */}
        {(activeMode === 'text-to-video' || activeMode === 'image-to-video') && (
          <div>
            <p className="text-xs text-gray-500 mb-2 font-medium">Camera Motion</p>
            <div className="flex flex-wrap gap-2">
              {['orbit', 'dolly-in', 'pan-left', 'crane-up', 'static'].map(m => (
                <button
                  key={m}
                  onClick={() => setCameraMotion(m)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cameraMotion === m ? 'bg-pink-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {m.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Duration */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-500 font-medium">Duration</p>
            <span className="text-xs text-pink-500 font-semibold">{duration}s</span>
          </div>
          <input
            type="range"
            min={3}
            max={15}
            value={duration}
            onChange={e => setDuration(Number(e.target.value))}
            className="w-full accent-pink-500"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>3s</span><span>15s</span>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
            isGenerating ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:shadow-lg hover:shadow-pink-500/30'
          }`}
        >
          {isGenerating ? (
            <><i className="ri-loader-4-line animate-spin"></i> Generating Video...</>
          ) : (
            <><i className="ri-film-line"></i> Generate Video</>
          )}
        </button>
      </div>

      {/* Jobs Queue */}
      {jobs.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Generated Videos</h4>
          {jobs.map(job => (
            <div key={job.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="flex gap-3 p-3">
                <div className="relative rounded-lg overflow-hidden flex-shrink-0" style={{ width: '100px', height: '60px' }}>
                  <img src={job.thumbnail} alt="Video thumbnail" className="w-full h-full object-cover" />
                  {job.status === 'done' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center">
                        <i className="ri-play-fill text-gray-900 text-sm ml-0.5"></i>
                      </div>
                    </div>
                  )}
                  {job.status === 'processing' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <i className="ri-loader-4-line animate-spin text-white text-lg"></i>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                      {VIDEO_MODES.find(m => m.id === job.mode)?.label}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                      job.status === 'done' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                      {job.status === 'done' ? 'Ready' : `${job.progress}%`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{job.prompt}</p>
                  {job.status === 'processing' && (
                    <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      ></div>
                    </div>
                  )}
                  {job.status === 'done' && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleDownloadVideo(job.thumbnail)}
                        className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-download-line text-xs"></i> Download
                      </button>
                      <button
                        onClick={() => handleShareVideo(job.thumbnail)}
                        className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-share-line text-xs"></i> Share
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Toast */}
      {videoToast && (
        <div className="fixed bottom-6 right-6 px-4 py-3 bg-gray-900 text-white rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-xs"></i>
          </div>
          <span className="text-sm font-medium">{videoToast}</span>
        </div>
      )}
    </div>
  );
}
